import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import Iconify from "src/components/iconify";
import { useDispatch, useSelector } from "react-redux";
import { setDialog } from "src/features/dialog/dialogSlice";
import {
  useUploadMailHtmlTemplateMutation,
  useDeleteMailHtmlTemplateMutation,
} from "src/features/tenants/tenantsApiSlice";

let SERVER_URL = "/api";
if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
  SERVER_URL = "http://localhost:5001/api";
}

// ---------------------------------------------------------------------------
// Token replacement for HTML preview (matches MailTemplatePageLayout logic)
// ---------------------------------------------------------------------------

const computeFormalGreeting = (member) => {
  const title = String(member?.title || "").trim().toLowerCase();
  const gender = String(member?.gender || "").trim().toLowerCase();
  const lastName = member?.lastName || "";
  const firstName = member?.firstName || "";
  const fullName = `${firstName} ${lastName}`.trim();

  if (title === "herr" || gender === "m" || gender === "male" || gender === "männlich") {
    return `Sehr geehrter Herr ${lastName}`;
  }
  if (title === "frau" || gender === "f" || gender === "female" || gender === "weiblich") {
    return `Sehr geehrte Frau ${lastName}`;
  }
  return `Guten Tag ${fullName}`;
};

const renderHtmlPreview = (html, member, tenantName) => {
  if (!html) return "";
  if (!member) return html;

  const today = new Date().toLocaleDateString("de-DE");
  const firstName = member?.firstName || "";
  const lastName = member?.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim();
  const contractEmail =
    member?.contractRef?.email || member?.mail || "[E-Mail-Adresse]";
  const contractOwner =
    (member?.contractRef?.firstName
      ? `${member.contractRef.firstName} ${member.contractRef.lastName || ""}`.trim()
      : null) ||
    fullName ||
    "[Vertragsinhaber]";

  return html
    .replace(/\{\{#hasReason\}\}([\s\S]*?)\{\{\/hasReason\}\}/g, "$1")
    .replace(/\{\{\^hasReason\}\}([\s\S]*?)\{\{\/hasReason\}\}/g, "")
    .replace(/\{\{#activationUrl\}\}([\s\S]*?)\{\{\/activationUrl\}\}/g, "$1")
    .replace(/\{\{formalGreeting\}\}/g, computeFormalGreeting(member))
    .replace(/\{\{memberFirstName\}\}/g, firstName)
    .replace(/\{\{memberLastName\}\}/g, lastName)
    .replace(/\{\{memberFullName\}\}/g, fullName)
    .replace(/\{\{contractOwner\}\}/g, contractOwner)
    .replace(/\{\{contractEmail\}\}/g, contractEmail)
    .replace(
      /\{\{activationUrl\}\}/g,
      "<em>[Aktivierungslink – wird beim echten Versand generiert]</em>"
    )
    .replace(/\{\{tenantName\}\}/g, tenantName || "[Vereinsname]")
    .replace(/\{\{today\}\}/g, today)
    .replace(/\{\{terminationDate\}\}/g, today)
    .replace(/\{\{lastMonth\}\}/g, "[Letzter Beitragsmonat]")
    .replace(/\{\{contractEndDate\}\}/g, "[Vertragsende-Datum]")
    .replace(/\{\{noticePeriod\}\}/g, "[3]")
    .replace(/\{\{contractDate\}\}/g, "[Vertragsbeginn-Datum]")
    .replace(/\{\{contractDuration\}\}/g, "[12]")
    .replace(/\{\{contractExtension\}\}/g, "[12]")
    .replace(
      /\{\{reason\}\}/g,
      "<em>[Ablehnungsgrund – wird vom Admin angegeben]</em>"
    );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const HtmlTemplateUploadSection = ({
  templateType,
  currentHtmlTemplate,
  members = [],
  tenantName = "",
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedPreviewMember, setSelectedPreviewMember] = useState(null);
  const token = useSelector((state) => state.auth.token);

  const [uploadHtmlTemplate, { isLoading: isUploading }] =
    useUploadMailHtmlTemplateMutation();
  const [deleteHtmlTemplate, { isLoading: isDeleting }] =
    useDeleteMailHtmlTemplateMutation();

  const isActive = Boolean((currentHtmlTemplate || "").trim());

  const previewHtml = useMemo(
    () => renderHtmlPreview(currentHtmlTemplate || "", selectedPreviewMember, tenantName),
    [currentHtmlTemplate, selectedPreviewMember, tenantName]
  );

  const handleDownloadExample = async () => {
    try {
      const res = await fetch(`${SERVER_URL}/tenant/mail-template-example`, {
        headers: { authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download fehlgeschlagen");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "mail-vorlage-beispiel.html";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      dispatch(
        setDialog({
          title: "Fehler",
          type: "ERROR",
          data: { errorText: "Beispiel-Vorlage konnte nicht heruntergeladen werden." },
        })
      );
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    try {
      const htmlContent = await selectedFile.text();
      const result = await uploadHtmlTemplate({ templateType, htmlContent }).unwrap();
      if (result?.type === "success") {
        dispatch(
          setDialog({
            title: "HTML-Vorlage hochgeladen",
            type: "SUCCESS",
            data: { successText: result.message },
          })
        );
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: "Fehler",
          type: "ERROR",
          data: {
            errorText:
              error?.data?.message || "HTML-Vorlage konnte nicht hochgeladen werden.",
          },
        })
      );
    }
  };

  const handleDelete = async () => {
    try {
      const result = await deleteHtmlTemplate({ templateType }).unwrap();
      if (result?.type === "success") {
        dispatch(
          setDialog({
            title: "HTML-Vorlage entfernt",
            type: "SUCCESS",
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: "Fehler",
          type: "ERROR",
          data: {
            errorText:
              error?.data?.message || "HTML-Vorlage konnte nicht entfernt werden.",
          },
        })
      );
    }
  };

  return (
    <>
      <Card
        sx={{
          borderRadius: 2,
          background:
            "linear-gradient(135deg, rgba(25,118,210,0.06) 0%, rgba(56,142,60,0.05) 100%)",
        }}
      >
        <CardHeader title="Eigene HTML-E-Mail-Vorlage (optional)" />
        <CardContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Laden Sie hier optional eine HTML-Vorlage hoch. Ob beim Versand
            HTML oder Text verwendet wird, steuern Sie über den
            <strong> Versandmodus </strong> auf dieser Seite. Im Modus
            <strong> Automatisch </strong> nutzt der Server HTML nur dann, wenn
            eine HTML-Vorlage hinterlegt ist, sonst die Text-Vorlage.
          </Alert>

          <Button
            variant="outlined"
            startIcon={<Iconify icon="mdi:download" />}
            onClick={handleDownloadExample}
            sx={{ mb: 3 }}
          >
            Beispiel-Vorlage herunterladen
          </Button>

          <Divider sx={{ my: 2 }} />

          {isActive ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Eine eigene HTML-Vorlage ist hinterlegt.
              </Alert>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  color="info"
                  onClick={() => setPreviewOpen(true)}
                  startIcon={<Iconify icon="mdi:eye" />}
                >
                  Vorschau
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  startIcon={<Iconify icon="mdi:delete" />}
                >
                  Entfernen
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keine eigene HTML-Vorlage hochgeladen.
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            HTML-Vorlage hochladen
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<Iconify icon="mdi:file-upload" />}
            >
              Datei auswählen (.html)
              <input
                ref={fileInputRef}
                type="file"
                accept=".html,.htm"
                hidden
                onChange={handleFileSelect}
              />
            </Button>
            {selectedFile && (
              <Typography variant="body2" color="text.secondary">
                {selectedFile.name}
              </Typography>
            )}
            <Button
              variant="contained"
              color="secondary"
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
            >
              Hochladen
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* HTML Preview dialog with member selection */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>HTML-Vorlage Vorschau</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Wählen Sie ein Mitglied aus, um die HTML-Vorlage mit echten Daten
            zu simulieren. Platzhalter werden durch Mitgliedsdaten ersetzt.
          </Typography>

          <Autocomplete
            options={members}
            getOptionLabel={(m) =>
              `${m?.firstName || ""} ${m?.lastName || ""}`.trim() || "—"
            }
            value={selectedPreviewMember}
            onChange={(_, v) => setSelectedPreviewMember(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Mitglied für Vorschau auswählen"
                size="small"
                sx={{ mb: 3 }}
              />
            )}
          />

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              overflow: "auto",
              maxHeight: "60vh",
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HtmlTemplateUploadSection;
